/* ============================================================
   Vegannett — theme.js
   Core utilities, section lifecycle, accordion, init
   ============================================================ */

'use strict';

/* ---- Accordion ---- */

function initAccordions(root = document) {
  root.querySelectorAll('.vn-accordion__trigger').forEach(trigger => {
    trigger.addEventListener('click', () => {
      const item = trigger.closest('.vn-accordion__item');
      const isOpen = item.classList.contains('is-open');
      // close all in same group
      const group = item.closest('.vn-accordion');
      if (group) {
        group.querySelectorAll('.vn-accordion__item.is-open').forEach(i => i.classList.remove('is-open'));
      }
      if (!isOpen) item.classList.add('is-open');
    });
  });
}

/* ---- Sticky header ---- */

function initStickyHeader() {
  const header = document.querySelector('.vn-header');
  if (!header) return;
  let lastY = 0;
  window.addEventListener('scroll', () => {
    const y = window.scrollY;
    if (y > 80) {
      header.classList.add('vn-header--scrolled');
      header.classList.toggle('vn-header--hidden', y > lastY + 5);
    } else {
      header.classList.remove('vn-header--scrolled', 'vn-header--hidden');
    }
    lastY = y;
  }, { passive: true });
}

/* ---- Notification toast ---- */

window.vnNotify = function(message, duration = 3000) {
  let el = document.querySelector('.vn-notification');
  if (!el) {
    el = document.createElement('div');
    el.className = 'vn-notification';
    document.body.appendChild(el);
  }
  el.textContent = message;
  el.classList.add('is-visible');
  clearTimeout(el._timer);
  el._timer = setTimeout(() => el.classList.remove('is-visible'), duration);
};

/* ---- Image lazy load fallback ---- */

function initLazyImages() {
  if ('loading' in HTMLImageElement.prototype) return; // native support
  document.querySelectorAll('img[loading="lazy"]').forEach(img => {
    if (img.dataset.src) img.src = img.dataset.src;
  });
}

/* ---- Intersection Observer: fade-in ---- */

/* Idempotent: jedes Element wird genau einmal gebunden (data-reveal-bound).
   Laeuft zusaetzlich bei shopify:section:load, sonst bleibt jede im
   Theme-Editor neu hinzugefuegte Section auf opacity 0 stehen. */
function initFadeIn(root = document) {
  const scope = root && root.querySelectorAll ? root : document;
  const els = scope.querySelectorAll('[data-reveal]:not([data-reveal-bound])');
  if (!els.length) return;
  els.forEach(el => el.setAttribute('data-reveal-bound', ''));

  // Respect prefers-reduced-motion: reveal all immediately, no animation
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    els.forEach(el => el.classList.add('is-visible'));
    return;
  }

  const io = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (e.isIntersecting) {
        const delay = e.target.dataset.revealDelay;
        if (delay) {
          setTimeout(() => e.target.classList.add('is-visible'), (parseInt(delay) - 1) * 120);
        } else {
          e.target.classList.add('is-visible');
        }
        io.unobserve(e.target);
      }
    });
  }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });

  // Elements already in viewport at load time: reveal immediately, don't observe
  els.forEach(el => {
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      el.classList.add('is-visible');
    } else {
      io.observe(el);
    }
  });
}

/* ---- Hero Parallax ---- */

function initHeroParallax() {
  const hero = document.querySelector('.vn-hero');
  const img  = hero && hero.querySelector('.vn-hero__img');
  if (!img || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  // Mark loaded so scale transition fires
  hero.classList.add('is-loaded');

  let ticking = false;
  window.addEventListener('scroll', () => {
    if (ticking) return;
    requestAnimationFrame(() => {
      const y = window.scrollY;
      const limit = hero.offsetHeight;
      if (y <= limit) {
        img.style.transform = `scale(1.08) translateY(${y * 0.25}px)`;
      }
      ticking = false;
    });
    ticking = true;
  }, { passive: true });
}

/* ---- Card hover lift ---- */

function initCardHover() {
  document.querySelectorAll('.vn-card').forEach(card => {
    card.addEventListener('mouseenter', () => {
      card.style.transform = 'translateY(-6px)';
      card.style.transition = 'transform 0.3s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease';
      card.style.boxShadow = '0 20px 40px rgba(0,0,0,0.35), 0 0 0 1px rgba(212,184,150,0.15)';
    });
    card.addEventListener('mouseleave', () => {
      card.style.transform = '';
      card.style.boxShadow = '';
    });
  });
}

/* ---- USP icon pulse on reveal ---- */

function initUspPulse() {
  const io = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (!e.isIntersecting) return;
      const icons = e.target.querySelectorAll('.vn-usp-item__icon');
      icons.forEach((icon, i) => {
        setTimeout(() => {
          icon.style.animation = 'vnPulse 0.5s cubic-bezier(0.34,1.56,0.64,1)';
          setTimeout(() => icon.style.animation = '', 600);
        }, i * 100);
      });
      io.unobserve(e.target);
    });
  }, { threshold: 0.4 });
  document.querySelectorAll('.vn-usp-section').forEach(el => io.observe(el));
}

/* ---- Init ---- */

function vnInit() {
  initAccordions();
  initStickyHeader();
  initLazyImages();
  initFadeIn();
  initHeroParallax();
  initCardHover();
  initUspPulse();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', vnInit);
} else {
  vnInit();
}

/* Theme-Editor: neu geladene Sections nachtraeglich sichtbar machen.
   Der Guard verhindert doppeltes Binden, falls theme.js zweimal laedt. */
if (!window.__vnSectionLoadBound) {
  window.__vnSectionLoadBound = true;
  document.addEventListener('shopify:section:load', (e) => {
    initFadeIn(e.target);
    initAccordions(e.target);
  });
}
